import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "../../../../../auth/AuthContext";
import {
  applyCepMask,
  applyCnpjMask,
  cleanCnpj,
} from "../../../../../utils/format-utils";
import { getFundDbName } from "../../../../../utils/funds";
import { imileFranquiaService } from "../services/imile-franquia-service";
import { ImileFranquia } from "../types";
import {
  ImileFranquiaEditFormData,
  ImileFranquiaRegistrationFormData,
  imileFranquiaEditSchema,
  imileFranquiaRegistrationSchema,
  normalizeDsList,
} from "../utils/validation-schemas";

export const imileFranquiaKeys = {
  all: ["imile-franquias"] as const,
  list: (dbName: string) => [...imileFranquiaKeys.all, "list", dbName] as const,
};

export function useImileCadastroFranquia() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [editingFranquia, setEditingFranquia] = useState<ImileFranquia | null>(
    null
  );

  // getFundDbName e não useAvailableFunds(): o cadastro do entrego ainda faz a
  // request de /availableFunds, e é justamente o que o comentário de
  // utils/funds.ts descreve como problema — enquanto ela estava em voo o dbName
  // ficava vazio e a tela dizia "não há nada" antes do skeleton. Aqui já nasce
  // no padrão corrigido, usado pelas outras telas.
  const xamaDbName = getFundDbName("imile");

  const registrationForm = useForm<ImileFranquiaRegistrationFormData>({
    resolver: zodResolver(imileFranquiaRegistrationSchema),
    defaultValues: {
      cnpj: "",
      razaoSocial: "",
      cep: "",
      endereco: "",
      apelido: "",
      ds: "",
      emailContato: "",
      franquiaUuid: "",
      redutor: 1,
      nfsAtivo: false,
      nfsSrAtivo: false,
    },
  });

  const editForm = useForm<
    ImileFranquiaEditFormData,
    any,
    ImileFranquiaEditFormData
  >({
    resolver: zodResolver(imileFranquiaEditSchema),
    defaultValues: {
      apelido: "",
      ds: "",
      emailContato: "",
      nfsAtivo: false,
      nfsSrAtivo: false,
      franquiaUuid: "",
      redutor: 1,
    },
  });

  const {
    data: franquias = [],
    isPending,
    refetch,
  } = useQuery({
    queryKey: imileFranquiaKeys.list(xamaDbName),
    queryFn: async (): Promise<ImileFranquia[]> => {
      // O Viz recebe o FUNDO e concatena o stage do lado dele (contrato herdado
      // do gestorEntregoCadastroViz), por isso "xama" e não xamaDbName.
      const response = await imileFranquiaService.getFranquias({
        dbName: "xama",
      });

      if (response.data && Array.isArray(response.data.data)) {
        return response.data.data.map((f) => ({
          id: String(f.id),
          cnpj: applyCnpjMask(f.cnpj),
          razaoSocial: f.razao_social,
          cep: applyCepMask(f.cep),
          endereco: f.endereco,
          apelido: f.ops,
          ds: f.ds ?? "",
          // O backend já calcula ops_derivadas; o fallback cobre o caso de um
          // deploy do front à frente do backend.
          opsDerivadas:
            f.ops_derivadas ??
            normalizeDsList(f.ds ?? "").map((base) => `${f.ops}${base}`),
          emailContato: f.email_contato,
          criadoPor: f.created_by,
          dataCriacao: new Date(f.created_at).toLocaleDateString("pt-BR"),
          franquiaUuid: f.franquia_uuid || "",
          nfsAtivo: f.nfs_ativo ?? false,
          nfsSrAtivo: f.nfs_sr_ativo ?? false,
          redutor:
            f.redutor !== undefined && f.redutor !== null
              ? parseFloat(f.redutor.toString())
              : 1,
        }));
      }
      return [];
    },
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 10,
  });

  const registerMutation = useMutation({
    mutationFn: async (data: ImileFranquiaRegistrationFormData) => {
      if (!user?.email) {
        throw new Error("Usuário sem email na sessão");
      }

      return imileFranquiaService.registerFranquia({
        type: "cadastro",
        dbName: xamaDbName,
        cnpj: cleanCnpj(data.cnpj),
        razaoSocial: data.razaoSocial,
        cep: data.cep.replace(/\D/g, ""),
        endereco: data.endereco,
        apelido: data.apelido.trim().toLowerCase(),
        ds: normalizeDsList(data.ds).join(","),
        emailContato: data.emailContato,
        franquiaUuid: data.franquiaUuid,
        redutor: data.redutor !== undefined ? Number(data.redutor) : 1,
        nfsAtivo: data.nfsAtivo,
        nfsSrAtivo: data.nfsSrAtivo,
        criadoPor: user.email || user.name || "",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: imileFranquiaKeys.list(xamaDbName),
      });
      registrationForm.reset();
      refetch();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      franquiaId,
      data,
    }: {
      franquiaId: string;
      data: ImileFranquiaEditFormData;
    }) => {
      if (!user?.email) {
        throw new Error("Usuário sem email na sessão");
      }

      return imileFranquiaService.updateFranquia({
        type: "update",
        dbName: xamaDbName,
        id: Number(franquiaId),
        apelido: data.apelido.trim().toLowerCase(),
        ds: normalizeDsList(data.ds).join(","),
        emailContato: data.emailContato,
        nfsAtivo: data.nfsAtivo,
        nfsSrAtivo: data.nfsSrAtivo,
        franquiaUuid: data.franquiaUuid,
        redutor: data.redutor !== undefined ? Number(data.redutor) : 1,
        criadoPor: user.email || user.name || "",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: imileFranquiaKeys.list(xamaDbName),
      });
      setEditingFranquia(null);
      editForm.reset();
      refetch();
    },
  });

  const handleRegister = registrationForm.handleSubmit((data) => {
    registerMutation.mutate(data);
  });

  const handleEditClick = (franquia: ImileFranquia) => {
    setEditingFranquia(franquia);
    editForm.reset({
      apelido: franquia.apelido || "",
      ds: franquia.ds || "",
      emailContato: franquia.emailContato || "",
      nfsAtivo: franquia.nfsAtivo ?? false,
      nfsSrAtivo: franquia.nfsSrAtivo ?? false,
      franquiaUuid: franquia.franquiaUuid || "",
      redutor:
        franquia.redutor !== undefined && franquia.redutor !== null
          ? franquia.redutor
          : 1,
    });
  };

  const handleUpdate = editForm.handleSubmit((data) => {
    if (!editingFranquia) return;
    updateMutation.mutate({
      franquiaId: editingFranquia.id,
      data,
    });
  });

  const handleCancelEdit = () => {
    setEditingFranquia(null);
    editForm.reset();
  };

  return {
    registrationForm,
    editForm,
    franquias,
    // isPending e não isLoading: no react-query v5 isLoading = isPending &&
    // isFetching, e o estado vazio vaza na tela entre os dois.
    isLoading: isPending,
    isRegistering: registerMutation.isPending,
    isUpdating: updateMutation.isPending,
    editingFranquia,
    xamaDbName,
    handleRegister,
    handleEditClick,
    handleUpdate,
    handleCancelEdit,
    registerError: registerMutation.error,
    updateError: updateMutation.error,
    registerSuccess: registerMutation.isSuccess,
    updateSuccess: updateMutation.isSuccess,
  };
}
